import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Skeleton } from 'antd';
import { setCurrentServingOrder } from '@/store/service';
import { MassageControlValueContext } from '@/pages/service/massage-control';
import service from '@/service';

import cx from './index.module.scss';

export type IssueDetectStep = 'DetectZeroDrift' | 'InstallDetectTools' | 'PlacePadQRCode';

interface Props {
  className?: string;
  currentPageStep?: Service.RealStep | IssueDetectStep;
  isIssueDetectSteps?: boolean;
}

const STEP_ID_TO_TEXT: Record<Service.RealStep | IssueDetectStep, string> = {
  ActionTest: '疼痛记录与动作测试',
  MassageConfirming: '确认理疗单',
  MassageSetting: '设定理疗参数',
  Installation: '安装理疗末端',
  CustomPalpation: '自定义方案｜设定理疗参数',
  VisualCheck: '人体检测',
  VisualCheckAndPalpation: '人体检测及手诊',
  Massage: '开始服务',
  /** 软硬件检测步骤 */
  DetectZeroDrift: '检测零飘偏差',
  InstallDetectTools: '安装排查工具末端',
  PlacePadQRCode: '放置平板识别码',
  BodyMeasurement: '围度测量',
};

const ServiceProcessCrumb: React.FC<Props> = ({ className, currentPageStep, isIssueDetectSteps = false }) => {
  const { massageState } = useContext(MassageControlValueContext);
  const [allSteps, setAllSteps] = useState(
    isIssueDetectSteps ? ['DetectZeroDrift', 'InstallDetectTools', 'PlacePadQRCode'] : []
  );
  const [currentStep, setCurrentStep] = useState(currentPageStep);
  const dispatch = useDispatch();
  const currentDeviceId = useRef(null);

  const currentIndex = useMemo(
    () => allSteps?.findIndex(item => item === (isIssueDetectSteps ? currentPageStep : currentStep)),
    [allSteps, currentPageStep, currentStep, isIssueDetectSteps]
  );

  const currentIsCustomPalpation = useMemo(
    () =>
      [
        'Preparing_WaitingDrawCustomRoute',
        'Preparing_DrawCustomRoute',
        'Preparing_WaitingTechRouteGenerating',
        'Preparing_Setting',
      ].includes(massageState),
    [massageState]
  );

  const initData = useCallback(async () => {
    const { data: order } = await service.service.getServingOrderByOperator();
    dispatch(setCurrentServingOrder(order));
    currentDeviceId.current = order?.deviceId;

    const _allSteps = order?.stepFlow?.reduce((accur, cur) => {
      if (cur.step === 'RoboticTask') {
        accur.push(...cur.subStep);
      } else {
        accur.push(cur.step);
      }
      return accur;
    }, []);
    setAllSteps(_allSteps);

    // 如果显示设置了展示的步骤则不从服务器获取
    if (!currentPageStep) {
      // 如果当前步骤在 RoboticTask 则从局域网获取实际步骤
      if (order?.step === 'RoboticTask') {
        const stepInfo = await service.lanInterface.getCurrentStep(order?.deviceId);
        setCurrentStep(stepInfo?.step);
      } else {
        setCurrentStep(order?.step);
      }
    }
  }, [currentPageStep, dispatch]);

  const updateCurrentStep = useCallback(async () => {
    if (!currentDeviceId.current) return;

    const stepInfo = await service.lanInterface.getCurrentStep(currentDeviceId.current);
    setCurrentStep(stepInfo?.step);
  }, []);

  useEffect(() => {
    !isIssueDetectSteps && initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentIsCustomPalpation) {
      updateCurrentStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIsCustomPalpation]);

  return (
    <div className={cx('service-process-crumb', className)}>
      <Skeleton loading={!currentStep} paragraph={false} avatar={false} active={true}>
        {allSteps?.map((item, index) => (
          <div
            className={cx('process-item', item, {
              current: currentIndex === index,
              'is-prev-process': currentIndex > index,
              'is-later-process': currentIndex < index,
              'is-first': index === 0,
              'is-last': index === allSteps?.length - 1,
            })}
            key={item}
          >
            {STEP_ID_TO_TEXT[item]}
            <div className={cx('mirror-bg', 'mirror-top')}></div>
            <div className={cx('mirror-bg', 'mirror-bottom')}></div>
          </div>
        ))}
      </Skeleton>
    </div>
  );
};

export default ServiceProcessCrumb;
